import { RECTANGLE_PREFIX_ID } from '@frontend/constants/templates-field-prefixes';
import { PRIMARY_MODE, SECONDARY_MODE } from '@frontend/group/modules/dominant-colors';
import { scaleControlsCornerSize } from '@frontend/constants/fabric-constants';
import {
  getLockValue,
  getPositioningOption,
  getPrimarySecondaryColorFromTheme,
  getShadowFabricInstance,
  getShapeFillStyle,
  isInGroup
} from '@frontend/utils';
import { fabric } from 'fabric';
import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';
import {ThemesContainerSingleton} from "@/banner_template/themes/ThemesContainerSinglton";
import { getGroupNameByAddFieldId } from "@/common/preview/draw-smart-object";
import { parseJsonData } from "@frontend/services/helpers";
import {parseShapeSettings} from "@frontend/group/modules/brand-colors/helper";
import {deleteBaseLayer} from "@/common/preview/common-helper";

const getScopePage = () => ShowPageScopeCollector.get(['canvas_data']);
const RELATED_FIELD_DEFAULT = {
  field: '',
  margin: 5,
  move_down_only: false
};

const parseRelatedFieldOptions = (options) => {
  const parsedOption = parseJsonData(options['Option2'], RELATED_FIELD_DEFAULT);

  if (!parsedOption || typeof parsedOption !== 'object' || Array.isArray(parsedOption)) {
    return { ...RELATED_FIELD_DEFAULT };
  }

  return {
    ...RELATED_FIELD_DEFAULT,
    ...parsedOption
  };
};

export async function drawRectangle({
   fields,
   positioning_options,
   data,
   canvas,
   id,
   group_fields,
   dimension,
   themes
}) {
  canvas.getObjects().forEach(function (o) {
    if (o.id.startsWith(RECTANGLE_PREFIX_ID)) {
      canvas.remove(o);
    }
  });

  const { canvas_data } = getScopePage();

  canvas_data[id].rectCoords = [];

  const selectedTheme = await ThemesContainerSingleton.getThemeById(data?.theme);

  let index = 0;
  fields.forEach((field) => {
    const options = JSON.parse(field.options);
    const groupName = getGroupNameByAddFieldId(
      String(field.id),
      canvas_data[id]
    );
    
    if (field.type === 'Rectangle' && (!group_fields || isInGroup(group_fields, field.name))) {
      const shapeSettings = parseShapeSettings(options);
      // Prefer the per-instance related field; fall back to the template's Option2.
      const relatedField = {
        ...RELATED_FIELD_DEFAULT,
        ...parseJsonData(data[`${field.element_id}_related_field`], parseRelatedFieldOptions(options)),
      };
      const optionStrokeColor = shapeSettings.stroke_color ?? options['Option1'];
      const optionFillColor = shapeSettings.fill_color ?? options['Option3'];
      const optionStrokeWidth = shapeSettings.stroke_width ?? options['Option2'] ?? 0;
      const optionRounding = shapeSettings.rounding ?? options['Option4'];
      let fill_color = data[`${field.element_id}_fill_color`];
      let stroke_color = data[`${field.element_id}_stroke_color`];

      /* This logic is to overrride the automatic color from the product images on feed import */
      if (optionFillColor === PRIMARY_MODE || optionFillColor === SECONDARY_MODE) {
        const theme_fill_color = getPrimarySecondaryColorFromTheme(optionFillColor, selectedTheme);
        // if a color was found on the theme then use it, otherwise keep the previously saved color
        if (theme_fill_color !== optionFillColor) {
          fill_color = theme_fill_color;
        }
      }

      if (optionStrokeColor === PRIMARY_MODE || optionStrokeColor === SECONDARY_MODE) {
        const theme_stroke_color = getPrimarySecondaryColorFromTheme(optionStrokeColor, selectedTheme);
        // if a color was found on the theme then use it, otherwise keep the previously saved color
        if (theme_stroke_color !== optionStrokeColor) {
          stroke_color = theme_stroke_color;
        }
      }

      const offset_x = parseInt(data[`${field.element_id}_offset_x`]);
      const offset_y = parseInt(data[`${field.element_id}_offset_y`]);
      const scaleX = parseFloat(data[`${field.element_id}_scaleX`]);
      const scaleY = parseFloat(data[`${field.element_id}_scaleY`]);
      const opacity = parseFloat(data[`${field.element_id}_opacity`]);
      const angle = parseInt(data[`${field.element_id}_angle`]);
      const visible = data[`${field.element_id}_visible`] ?? !options['Hidden'];
      const fillType = data[`${field.element_id}_fill_type`] ?? 'solid';
      const gradient = data[`${field.element_id}_gradient`] ?? null;

      let cornerOptions = [];
      
      if (optionRounding) {
        cornerOptions = $.map(
          optionRounding.toString().split(','),
          function (value) {
            return parseInt(value);
          }
        );
      }
      
      let radius = cornerOptions.length < 5 ? 0 : cornerOptions[4];
      const corners = cornerOptions.length < 5 ? [1, 1, 1, 1] : cornerOptions.slice(0, 4);
      
      if (cornerOptions.length === 1) {
        radius = parseInt(cornerOptions[0]);
      }
      
      radius = data[`${field.element_id}_radius`] ? parseInt(data[`${field.element_id}_radius`]) : radius;
      let thickness = optionStrokeWidth;
      thickness = +(data[`${field.element_id}_thickness`] ?? thickness);
      const toggleShape = data[`${field.element_id}_toggle_shape`] && data[`${field.element_id}_toggle_shape`] === 'on';

      if (toggleShape !== undefined && !toggleShape) {
        return;
      }
      
      let x = parseInt(options['X']);
      let y = parseInt(options['Y']);
      let width = +data[`${field.element_id}_width`] || +options['Width'];
      let height = +data[`${field.element_id}_height`] || +options['Height'];

      const positioningOption = getPositioningOption(positioning_options, data, fields, field);
      
      if (positioningOption) {
        x = positioningOption.x == null ? x : positioningOption.x;
        y = positioningOption.y == null ? y : positioningOption.y;
        width = positioningOption.width == null ? width : positioningOption.width;
      }

      const visualWidth = width;
      const visualHeight = height;

      // Adjust for Fabric's centered stroke:
      // visible size = inner size + strokeWidth
      // so we subtract strokeWidth to match desired outer dimensions.
      const innerWidth = visualWidth - thickness;
      const innerHeight = visualHeight - thickness;

      const rect = new fabric.CustomRect({
        id: RECTANGLE_PREFIX_ID + index,
        groupName: groupName,
        element_id: field.element_id,
        order: parseInt(options['Order']),
        top: y + offset_y,
        left: canvas_data[id].spacingFieldPosition[field.name]
          ? canvas_data[id].spacingFieldPosition[field.name].x
          : x + offset_x,
        width: innerWidth,
        height: innerHeight,
        fill: getShapeFillStyle({
          fillType,
          fill_color: fill_color
            ? fill_color
            : optionFillColor
              ? optionFillColor
              : '#ffffff',
          gradient
        }),
        fillType,
        gradient,
        stroke: stroke_color
          ? stroke_color
          : optionStrokeColor
            ? optionStrokeColor
            : '#ffffff',
        opacity: opacity
          ? opacity
          : options['Option5']
            ? options['Option5']
            : 1,
        shadow: getShadowFabricInstance(id, field.element_id),
        locked: getLockValue(id, field.element_id),
        strokeWidth: thickness,
        rx: radius,
        ry: radius,
        corners: corners,
        selectable: true,
        evented: true,
        fieldId: field.id,
        relatedField,
        templateField: canvas_data[id].templateInstance.getFieldById(field.id),
        updateLayoutChanges: canvas_data[id].updateLayoutChanges,
        deleteLayer: deleteBaseLayer,
        angle: angle,
        visible
      });

      let group_x = 0;
      let group_y = 0;
      
      if (groupName !== '' && canvas_data[id].smartObjCoords[groupName]) {
        group_x = parseInt(canvas_data[id].smartObjCoords[groupName]['X']);
        group_y = parseInt(canvas_data[id].smartObjCoords[groupName]['Y']);
      }
      
      rect.set({ left: rect.left + group_x - dimension[2] + dimension[4].x });
      rect.set({ top: rect.top + group_y - dimension[3] + dimension[4].y });
      
      scaleControlsCornerSize(rect);
      
      canvas.add(rect);
      
      canvas_data[id].rectCoords.push({
        x: x + group_x,
        y: y + group_y,
        scaleX: rect.scaleX / scaleX,
        scaleY: rect.scaleY / scaleY,
      });
      
      index++;
    }
  });
}
